import * as React from "react";
import { useState, useEffect, useMemo } from "react";
import classnames from "classnames";
import { TextArea, WarningBanner } from "@graffer-inc/console-design-kit";
import { BaseTypeLiterals } from "../../../../../application/domain/base/base-type";
import {
  ApplicationEntity,
  ApplyStatus,
} from "../../../../../application/domain/application/application-entity";
import { ApplicationsConst } from "../../../../../application/domain/application/const";
import { ApplicationStatusTag } from "../../../../molecules/status-tag";
import { BaseModal } from "../../../../atoms/base-modal";
import { emptyFunction } from "../../../../../utils/function-utils";

export type RevertStatusModalProps = {
  application: ApplicationEntity;
  isActive: boolean;
  procedureIsArchived: boolean;
  removalIsScheduled: boolean;
  onRevertStatusToWip: (internalComment: string) => Promise<void>;
  onClose: () => void;
};

function match<R>(
  state: "SELECT" | "CONFIRM",
  matcher: BaseTypeLiterals<"SELECT" | "CONFIRM", R>,
) {
  return matcher[state];
}

export const RevertStatusModal: React.FC<RevertStatusModalProps> = ({
  application,
  isActive,
  procedureIsArchived,
  removalIsScheduled,
  onRevertStatusToWip,
  onClose,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pageStatus, setPageState] = useState<"SELECT" | "CONFIRM">("SELECT");
  const [message, setMessage] = useState("");

  const isInvalidMessage = useMemo(
    () =>
      message.length === 0 ||
      !!message.match(/^\s+$/g) ||
      message.length > ApplicationsConst.INTERNAL_COMMENT_MAX_LENGTH,
    [message],
  );
  useEffect(() => {
    if (!isActive) {
      setIsSubmitting(false);
      setPageState("SELECT");
      setMessage("");
    }
  }, [isActive, setIsSubmitting, setPageState, setMessage]);

  const isRevertableApp = useMemo(() => {
    return !(procedureIsArchived || removalIsScheduled);
  }, [procedureIsArchived, removalIsScheduled]);

  return (
    <BaseModal
      isActive={isActive}
      title={match(pageStatus, {
        SELECT: "ステータスを戻す",
        CONFIRM: "更新内容を確認",
      })}
      onClose={onClose}
      hasOverflow={true}
      style={{ width: "700px" }}
    >
      {match(pageStatus, {
        SELECT: (
          <>
            <section className="modal-card-body">
              <div className="field is-horizontal">
                <div className="field-body">
                  <div className="field">
                    <label className="label m-2">
                      ステータスを「処理中」に戻すと、申請者の画面に表示されるステータスも、「処理中」に更新されます。
                    </label>
                    <WarningBanner
                      message={warningMessage}
                      description={warningDescription}
                    />
                  </div>
                </div>
              </div>
              <div className="field is-horizontal">
                <div className="field-body">
                  <div className="field">
                    <TextArea
                      onChange={(e) => {
                        setMessage(e.target.value);
                      }}
                      disabled={!isRevertableApp}
                      label={internalCommentLabel}
                      description={internalCommentDescription}
                      value={message}
                      errorMessages={
                        isInvalidMessage
                          ? `コメントは1文字以上、${ApplicationsConst.INTERNAL_COMMENT_MAX_LENGTH}文字以下の値を入力してください。`
                          : ""
                      }
                      maxLength={ApplicationsConst.INTERNAL_COMMENT_MAX_LENGTH}
                      required
                    />
                    <div className="level"></div>
                  </div>
                </div>
              </div>
              {procedureIsArchived && (
                <div className="has-text-danger mt-4">
                  手続きがアーカイブされているため、ステータスを戻すことはできません。
                </div>
              )}
              {removalIsScheduled && (
                <div className="has-text-danger mt-4">
                  削除が予定されている申請のステータスを戻すことはできません。
                </div>
              )}
            </section>
            <footer className="modal-card-foot">
              <button
                className="button is-failure is-fullwidth"
                onClick={onClose}
              >
                キャンセル
              </button>
              <button
                className={"button is-info is-fullwidth"}
                disabled={isInvalidMessage || !isRevertableApp}
                onClick={() => {
                  if (isInvalidMessage) return;
                  setPageState("CONFIRM");
                }}
              >
                更新内容の確認へ進む
              </button>
            </footer>
          </>
        ),
        CONFIRM: (
          <>
            <section className="modal-card-body">
              <div className="field">
                <WarningBanner
                  message={warningMessage}
                  description={warningDescription}
                />
              </div>
              <div className="field is-horizontal">
                <div className="field-body">
                  <div className="field">
                    <label htmlFor="name" className="label">
                      現在のステータス
                    </label>
                    <div className="control">
                      <ApplicationStatusTag
                        hiddenCheckIcon
                        status={application.status}
                      />
                    </div>
                  </div>
                  <div className="field">
                    <label htmlFor="name" className="label">
                      更新後のステータス
                    </label>
                    <div className="control">
                      <ApplicationStatusTag
                        hiddenCheckIcon
                        status={"WIP" as ApplyStatus}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="field">
                <TextArea
                  onChange={emptyFunction}
                  label={internalCommentLabel}
                  description={internalCommentDescription}
                  value={message}
                  required
                  disabled={true}
                />
              </div>
            </section>
            <footer className="modal-card-foot">
              <button
                className="button is-failure is-fullwidth"
                onClick={onClose}
                disabled={isSubmitting}
              >
                キャンセル
              </button>
              <button
                className={classnames("button is-info is-fullwidth", {
                  "is-loading": isSubmitting,
                })}
                onClick={async () => {
                  if (isSubmitting) {
                    return;
                  }
                  setIsSubmitting(true);
                  try {
                    await onRevertStatusToWip(message);
                    onClose();
                  } finally {
                    setIsSubmitting(false);
                  }
                }}
              >
                この内容で更新する
              </button>
            </footer>
          </>
        ),
      })}
    </BaseModal>
  );
};

const internalCommentLabel = "内部管理用コメント";
const internalCommentDescription =
  "内部用のコメントを記載できます。申請者には通知されません。";
const warningMessage =
  "以下のような影響があるため、操作を行う前に確認してください。";
const warningDescription = [
  "", // 見た目上の調整のため、改行を入れる
  "・通常のステータス更新の場合、申請者にメール通知が送信されますが、この操作はイレギュラーな処理のため、更新しても通知は送信されません。更新する前に、申請者にステータスが変更となる旨を伝えておくなどの対応を検討してください。",
  "・決済が発生する申請の場合、ステータスを「処理中」に戻しても自動的に返金処理が行われることはありません。返金や追加決済が必要な場合には、「処理中」に戻した後、必要に応じて決済処理を行ってください。",
].join("\n");
